/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */
export default{
	"@metadata": {
		"author": "Ellen Spertus <ellen.spertus@gmail.com>",
		"lastupdated": "2020-12-17 12:10:29.080541",
		"locale": "en",
		"messagedocumentation" : "qqq"
	},
	
    // custom tooltips for disabled blocks
	"OPTICAL_DATA_BLOCK_TOOLTIP": "Choose a satellite that collects images in the visible and infrared.",
	"SINGLE_BAND_MATH_BLOCK_TOOLTIP": "This block allows you to view a single optical image band as a black and white image. It will put the single data band into each of the red, green and blue outputs. Use <Other data visualisation> to visualise results. AVAILABLE IN LICENSED VERSION",
	"BAND_MATH_BLOCK_TOOLTIP":"This block allows you to combine bands together in different ways using arithmetic functions you define yourself using the arithmetic blocks. Use <Other data visualisation> to visualise results. AVAILABLE IN LICENSED VERSION",
	"BAND_INPUT_TOOLTIP":"Optical/radar data band selection. AVAILABLE IN LICENSED VERSION ",
	"UNSUPERVISED_BLOCK_TOOLTIP":"This block classifies the image based on the natural groupings in the data. This may help identify common features over an area. Best used on smaller areas.  AVAILABLE IN LICENSED VERSION",
	"SUPERVISED_BLOCK_TOOLTIP":"Supervised classification allows you to group areas within the image that have similar image properties as defined by you. Select known areas using the ADD AREA function on the map. The classification then finds other features that are the same as these sample training areas. AVAILABLE IN LICENSED VERSION",
	"SUPERVISED_CLASS_BLOCK_TOOLTIP":"You can select known areas as training data so that the classification finds other parts of the image that are the same as these samples. AVAILABLE IN LICENSED VERSION ",
	"CHANGE_DETECTION_BLOCK_SIMPLE_TOOLTIP": "Change detection. Differencing is applied to one band at a time or an index. Threshold differencing identifies areas above and below the specified threshold. It identifies them as positive change, no change, and negative change areas between the two time intervals. \nPlease place within a sensor and satellite C block to customise. AVAILABLE IN LICENSED VERSION",
	"CHANGE_DETECTION_BAND_TOOLTIP": "Band selection for change detection. AVAILABLE IN LICENSED VERSION ",
	"CHARTS_BLOCK_TOOLTIP": "Present results as a chart. AVAILABLE IN LICENSED VERSION ",
	"TABLE_BLOCK_TOOLTIP": "Present results as a table. AVAILABLE IN LICENSED VERSION (see Fires-Landcover example workflow for a demonstration)",
	"HISTOGRAM_BLOCK_TOOLTIP": "Creates a histogram which shows the statistical frequency of pixel values within the data.  AVAILABLE IN LICENSED VERSION ",

    // custom tooltips for enabled trial blocks
    // INPUTS
    // - DATE/AREA
    "BOUNDARIES_BLOCK_TOOLTIP": "Use to select an area of interest.",
    "DATES_BLOCK_TOOLTIP": "Use to select a date range.",
    "DATE_AREA_BLOCK_TOOLTIP": "Use to select an area of interest and date range",
    // - IMAGERY
    "SATELLITE_BLOCK_TOOLTIP": "Use to select data from a satellite with a visible or infrared sensor.",
    "RADAR_ONLY_BLOCK_TOOLTIP": "Use to select data from a radar satellite. Note that Sentinel-1 is only HH and HV polarization in polar regions, and VV and VH elsewhere.",
    "RADAR_BLOCK_TOOLTIP": "Choose data from radar imaging satellites. The polarization options are:\n1. VV: co-polarization, vertical transmit/vertical receive\n2. HH: co-polarization, horizontal transmit/horizontal receive\n3. VH: vertical transmit/horizontal receive\n4. HV: horizontal transmit/vertical receive \nNote that Sentinel-1 is only HH & HV polarization in polar regions, and VV & VH elsewhere.",
    // - TERRAIN
    "DEM_BLOCK_TOOLTIP": "Choose a digital elevation model (DEM). ",
    // - ATMOSPHERE
    "PRECIPITATION_DATA_BLOCK_TOOLTIP": "Precipitation data selection block. ",
    "PRECIPITATION_DATA_ONLY_BLOCK_TOOLTIP": "Precipitation data selection block. ",
    "CLIMATE_DATA_BLOCK_TOOLTIP": "Climate and weather data selection block. Smaller date ranges perform best. ",
    "CLIMATE_DATA_ONLY_BLOCK_TOOLTIP": "Climate and weather data selection block. Smaller date ranges perform best. ",
    "ATMOSPHERIC_BLOCK_TOOLTIP": "Other geophysical data selection block. ",
    "ATMOSPHERIC_DATA_BLOCK_TOOLTIP": "Atmospheric data selection block. ",
    // - ANTHROPOGENIC
    "ANTHROPOGENIC_BLOCK_TOOLTIP": "Anthropogenic data selection block. Includes population, settlement and human modification layers. ",
    // - ENVIRONMENT
    // -- Landcover
    "LAND_COVER_BLOCK_TOOLTIP": "Choose from a variety of land cover and land use data layers. ",
    "ICE_BLOCK_TOOLTIP": "Glacier data selection block. ",
    // -- Forest
    "FOREST_DATA_BLOCK_TOOLTIP": "View forest loss & gain from various sources, including Hansen and the PALSAR yearly mosaic. ",
    "FOREST_DATA_ONLY_BLOCK_TOOLTIP": "View forest loss & gain from various sources, including Hansen and the PALSAR yearly mosaic. ",
	"HANSEN_DATA_BLOCK_TOOLTIP": "Hansen Global Forest Change",
    // -- Water
    "WATER_BLOCK_TOOLTIP": "Surface water data selection block. ",
    "RIVERS_BLOCK_TOOLTIP": "River data selection block. Includes river networks and basins. ",
    "WATER_ONLY_BLOCK_TOOLTIP": "Surface water data selection block. ",
    "RIVERS_ONLY_BLOCK_TOOLTIP": "River data selection block. Includes river networks and basins. ",
    // -- Fires and Droughts
    "FIRES_BLOCK_TOOLTIP": `
    Choose data layers related to fires:
    - Annual Burnt Area, based on the MODIS Burned Area Monthly Global 500m dataset,
    - Fires Temperature, based on the Fire Information for Resource Management System dataset,
    - Fires Confidence, based on the Terra Thermal Anomalies & Fire Daily Global 1km dataset,
    Land Cover Breakdown calculated using the 2019 data from the Copernicus Global Land Cover Layers: CGLS-LC100 Collection 3 dataset`,
    "FIRES_ONLY_BLOCK_TOOLTIP": "Choose data layers related to fires, including the MODIS daily active fires product.",
    "DROUGHTS_BLOCK_TOOLTIP": "Droughts data selection block. ",

    // ANALYSE
    // - Comparison
    // TODO comparison currently has completely custom tooltips which should be imported to here
    // - Time Series
    "TIMESERIES_BLOCK_TOOLTIP": "Imagery time series block",
    // - Indices
    "NDVI_BLOCK_TOOLTIP": "Normalized Difference Vegetation Index. This is an indicator of vegetation cover and health, useful for looking at changes in vegetation cover over time. Requires the 'Other data visualisation' Output Block.",
    "NDWI_BLOCK_TOOLTIP": "Normalized Difference Water Index. An indicator of water content in water bodies. Uses green and NIR spectral bands. Requires the 'Other data visualisation' Output Block. ",
    "NDSI_BLOCK_TOOLTIP": "Normalized Difference Snow Index. An indicator of snow cover in a given area. Requires the 'Other data visualisation' Output Block. ",
    "NBI_BLOCK_TOOLTIP": "Normalized Burn Index. Used for identifying areas of burnt vegetation.  Requires the 'Other data visualisation' Output Block.",
    "SPI_BLOCK_TOOLTIP": "Standardised Precipitation Index. An indicator of relative precipitation. Requires the 'Other data visualisation' Output Block. ",

    // OUTPUT
    // - Map Visualisation
    "ADDLAYER_BLOCK_TOOLTIP": "Use this block to visualise data from optical sensors. Use the RGB options to create a colour composite image with each of the bands you choose being allocated to the three colour channels.",
    "RADAR_VIS_BLOCK_TOOLTIP": "Use this block to visualise data from radar sensors. Use the RGB options to create a colour composite image with each of the polarisation bands being allocated to the three colour channels. \nNote that Sentinel-1 is only available as HH & HV polarization in polar regions, and is only available as VV & VH elsewhere. ",
    "DEM_VIS_BLOCK_TOOLTIP": "Digital elevation model visualisation. Slope is the steepness of a surface, computed as the maximum rate of change of elevation between that location and adjacent pixels. Slope is expressed in degrees. Aspect is the orientation of slope, measured clockwise in degrees from the north. Hillshade maps the terrain using light and shadow to create a 3D-looking image.",
    "FOREST_VIS_BLOCK_TOOLTIP": "Forest change data visualisation",
    "OTHER_DATA_VIS_BLOCK_TOOLTIP": "Use this block to visualise all data that is not created from the optical and radar imagery, or DEM blocks. ",
    // - Download
    "DOWNLOAD_BLOCK_TOOLTIP": "Download data with custom file name and type",

    // OTHER
    "CLOUD_FILTER_BLOCK_TOOLTIP": "Remove clouds for optical remote sensing data only. Choose from various different algorithms.",
    "SCATTER_Y_BAND_INPUT_BLOCK_TOOLTIP": "Band input for y axis",
    "PCA_BLOCK_TOOLTIP": "Data dimensionality reduction technique that transforms an original correlated dataset into a smaller set of uncorrelated variables that represent most of the information present in the dataset",
    "TCA_BLOCK_TOOLTIP": "Transformation of the image original bands into brightness, greeness and wetness bands that are useful for vegetation mapping",

    // FIELDS
    // used in dem, download, other vis, radar vis, vis,
    "FILE_NAME_TOOLTIP": "Set a custom file name. Limited to 20 characters.",
    "LAYER_NAME_TOOLTIP": "Enter a custom layer name. Limited to 20 characters.",
    
    // COMPARISON
    "COMPARISON_BLOCK_TOOLTIP": "Land comparison block. Please place within a parent block to use. \nKey: ", 
    // special case for all comparison labels
    'COPERNICUS_COVER_2015': '\n0: Unknown \n20: Shrubs. \n30: Herbaceous vegetation. \n40: Cultivated and managed vegetation/agriculture (cropland). \n50: Urban / built up. \n60: Bare / sparse vegetation. \n70: Snow and ice. \n80: Permanent water bodies. \n90: Herbaceous wetland. \n100: Moss and lichen. \n111: Closed forest, evergreen needle leaf.  \n112: Closed forest, evergreen broad leaf. \n113: Closed forest, deciduous needle leaf.  \n114: Closed forest, deciduous broad leaf. \n115: Closed forest, mixed. \n116: Closed forest, unknown. \n121: Open forest, evergreen needle leaf. ' + '\n122: Open forest, evergreen broad leaf. \n123: Open forest, deciduous needle leaf. \n124: Open forest, deciduous broad leaf. \n125: Open forest, mixed. \n126: Open forest, unknown. \n200: Open sea.',
    'COPERNICUS_COVER_2016': '\n0: Unknown \n20: Shrubs. \n30: Herbaceous vegetation. \n40: Cultivated and managed vegetation/agriculture (cropland). \n50: Urban / built up. \n60: Bare / sparse vegetation. \n70: Snow and ice. \n80: Permanent water bodies. \n90: Herbaceous wetland. \n100: Moss and lichen. \n111: Closed forest, evergreen needle leaf.  \n112: Closed forest, evergreen broad leaf. \n113: Closed forest, deciduous needle leaf.  \n114: Closed forest, deciduous broad leaf. \n115: Closed forest, mixed. \n116: Closed forest, unknown. \n121: Open forest, evergreen needle leaf. ' + '\n122: Open forest, evergreen broad leaf. \n123: Open forest, deciduous needle leaf. \n124: Open forest, deciduous broad leaf. \n125: Open forest, mixed. \n126: Open forest, unknown. \n200: Open sea.',
    'COPERNICUS_COVER_2017': '\n0: Unknown \n20: Shrubs. \n30: Herbaceous vegetation. \n40: Cultivated and managed vegetation/agriculture (cropland). \n50: Urban / built up. \n60: Bare / sparse vegetation. \n70: Snow and ice. \n80: Permanent water bodies. \n90: Herbaceous wetland. \n100: Moss and lichen. \n111: Closed forest, evergreen needle leaf.  \n112: Closed forest, evergreen broad leaf. \n113: Closed forest, deciduous needle leaf.  \n114: Closed forest, deciduous broad leaf. \n115: Closed forest, mixed. \n116: Closed forest, unknown. \n121: Open forest, evergreen needle leaf. ' + '\n122: Open forest, evergreen broad leaf. \n123: Open forest, deciduous needle leaf. \n124: Open forest, deciduous broad leaf. \n125: Open forest, mixed. \n126: Open forest, unknown. \n200: Open sea.',
    'COPERNICUS_COVER_2018': '\n0: Unknown \n20: Shrubs. \n30: Herbaceous vegetation. \n40: Cultivated and managed vegetation/agriculture (cropland). \n50: Urban / built up. \n60: Bare / sparse vegetation. \n70: Snow and ice. \n80: Permanent water bodies. \n90: Herbaceous wetland. \n100: Moss and lichen. \n111: Closed forest, evergreen needle leaf.  \n112: Closed forest, evergreen broad leaf. \n113: Closed forest, deciduous needle leaf.  \n114: Closed forest, deciduous broad leaf. \n115: Closed forest, mixed. \n116: Closed forest, unknown. \n121: Open forest, evergreen needle leaf. ' + '\n122: Open forest, evergreen broad leaf. \n123: Open forest, deciduous needle leaf. \n124: Open forest, deciduous broad leaf. \n125: Open forest, mixed. \n126: Open forest, unknown. \n200: Open sea.',
    'COPERNICUS_COVER_2019': '\n0: Unknown \n20: Shrubs. \n30: Herbaceous vegetation. \n40: Cultivated and managed vegetation/agriculture (cropland). \n50: Urban / built up. \n60: Bare / sparse vegetation. \n70: Snow and ice. \n80: Permanent water bodies. \n90: Herbaceous wetland. \n100: Moss and lichen. \n111: Closed forest, evergreen needle leaf.  \n112: Closed forest, evergreen broad leaf. \n113: Closed forest, deciduous needle leaf.  \n114: Closed forest, deciduous broad leaf. \n115: Closed forest, mixed. \n116: Closed forest, unknown. \n121: Open forest, evergreen needle leaf. ' + '\n122: Open forest, evergreen broad leaf. \n123: Open forest, deciduous needle leaf. \n124: Open forest, deciduous broad leaf. \n125: Open forest, mixed. \n126: Open forest, unknown. \n200: Open sea.',

    // default blockly tooltips
	"VARIABLES_DEFAULT_NAME": "item",
	"UNNAMED_KEY": "unnamed",
	"TODAY": "Today",
	"DUPLICATE_BLOCK": "Duplicate",
	"ADD_COMMENT": "Add Comment",
	"REMOVE_COMMENT": "Remove Comment",
	"DUPLICATE_COMMENT": "Duplicate Comment",
	"EXTERNAL_INPUTS": "External Inputs",
	"INLINE_INPUTS": "Inline Inputs",
	"DELETE_BLOCK": "Delete Block",
	"DELETE_X_BLOCKS": "Delete %1 Blocks",
	"DELETE_ALL_BLOCKS": "Delete all %1 blocks?",
	"CLEAN_UP": "Clean up Blocks",
	"COLLAPSE_BLOCK": "Collapse Block",
	"COLLAPSE_ALL": "Collapse Blocks",
	"EXPAND_BLOCK": "Expand Block",
	"EXPAND_ALL": "Expand Blocks",
	"DISABLE_BLOCK": "Disable Block",
	"ENABLE_BLOCK": "Enable Block",
	"HELP": "Help",
	"UNDO": "Undo",
	"REDO": "Redo",
	"CHANGE_VALUE_TITLE": "Change value:",
	"RENAME_VARIABLE": "Rename variable...",
	"RENAME_VARIABLE_TITLE": "Rename all '%1' variables to:",
	"NEW_VARIABLE": "Create variable...",
	"NEW_STRING_VARIABLE": "Create string variable...",
	"NEW_NUMBER_VARIABLE": "Create number variable...",
	"NEW_COLOUR_VARIABLE": "Create colour variable...",
	"NEW_VARIABLE_TYPE_TITLE": "New variable type:",
	"NEW_VARIABLE_TITLE": "New variable name:",
	"VARIABLE_ALREADY_EXISTS": "A variable named '%1' already exists.",
	"VARIABLE_ALREADY_EXISTS_FOR_ANOTHER_TYPE": "A variable named '%1' already exists for another type: '%2'.",
	"DELETE_VARIABLE_CONFIRMATION": "Delete %1 uses of the '%2' variable?",
	"CANNOT_DELETE_VARIABLE_PROCEDURE": "Can't delete the variable '%1' because it's part of the definition of the function '%2'",
	"DELETE_VARIABLE": "Delete the '%1' variable",
	"COLOUR_PICKER_HELPURL": "https://en.wikipedia.org/wiki/Color",
	"COLOUR_PICKER_TOOLTIP": "Choose a colour from the palette.",
	"COLOUR_RANDOM_HELPURL": "http://randomcolour.com",
	"COLOUR_RANDOM_TITLE": "random colour",
	"COLOUR_RANDOM_TOOLTIP": "Choose a colour at random.",
	"COLOUR_RGB_HELPURL": "https://www.december.com/html/spec/colorpercompact.html",
	"COLOUR_RGB_TITLE": "colour with",
	"COLOUR_RGB_RED": "red",
	"COLOUR_RGB_GREEN": "green",
	"COLOUR_RGB_BLUE": "blue",
	"COLOUR_RGB_TOOLTIP": "Create a colour with the specified amount of red, green, and blue. All values must be between 0 and 100.",
	"COLOUR_BLEND_HELPURL": "https://meyerweb.com/eric/tools/color-blend/#:::rgbp",
	"COLOUR_BLEND_TITLE": "blend",
	"COLOUR_BLEND_COLOUR1": "colour 1",
	"COLOUR_BLEND_COLOUR2": "colour 2",
	"COLOUR_BLEND_RATIO": "ratio",
	"COLOUR_BLEND_TOOLTIP": "Blends two colours together with a given ratio (0.0 - 1.0).",
	"CONTROLS_REPEAT_HELPURL": "https://en.wikipedia.org/wiki/For_loop",
	"CONTROLS_REPEAT_TITLE": "repeat %1 times",
	"CONTROLS_REPEAT_INPUT_DO": "do",
	"CONTROLS_REPEAT_TOOLTIP": "Do some statements several times.",
	"CONTROLS_WHILEUNTIL_HELPURL": "https://github.com/google/blockly/wiki/Loops#repeat",
	"CONTROLS_WHILEUNTIL_OPERATOR_WHILE": "repeat while",
	"CONTROLS_WHILEUNTIL_OPERATOR_UNTIL": "repeat until",
	"CONTROLS_WHILEUNTIL_TOOLTIP_WHILE": "While a value is true, then do some statements.",
	"CONTROLS_WHILEUNTIL_TOOLTIP_UNTIL": "While a value is false, then do some statements.",
	"CONTROLS_FOR_HELPURL": "https://github.com/google/blockly/wiki/Loops#count-with",
	"CONTROLS_FOR_TOOLTIP": "Have the variable '%1' take on the values from the start number to the end number, counting by the specified interval, and do the specified blocks.",
	"CONTROLS_FOR_TITLE": "count with %1 from %2 to %3 by %4",
	"CONTROLS_FOREACH_HELPURL": "https://github.com/google/blockly/wiki/Loops#for-each",
	"CONTROLS_FOREACH_TITLE": "for each item %1 in list %2",
	"CONTROLS_FOREACH_TOOLTIP": "For each item in a list, set the variable '%1' to the item, and then do some statements.",
	"CONTROLS_FLOW_STATEMENTS_HELPURL": "https://github.com/google/blockly/wiki/Loops#loop-termination-blocks",
	"CONTROLS_FLOW_STATEMENTS_OPERATOR_BREAK": "break out of loop",
	"CONTROLS_FLOW_STATEMENTS_OPERATOR_CONTINUE": "continue with next iteration of loop",
	"CONTROLS_FLOW_STATEMENTS_TOOLTIP_BREAK": "Break out of the containing loop.",
	"CONTROLS_FLOW_STATEMENTS_TOOLTIP_CONTINUE": "Skip the rest of this loop, and continue with the next iteration.",
	"CONTROLS_FLOW_STATEMENTS_WARNING": "Warning: This block may only be used within a loop.",
	"CONTROLS_IF_HELPURL": "https://github.com/google/blockly/wiki/IfElse",
	"CONTROLS_IF_TOOLTIP_1": "If a value is true, then do some statements.",
	"CONTROLS_IF_TOOLTIP_2": "If a value is true, then do the first block of statements. Otherwise, do the second block of statements.",
	"CONTROLS_IF_TOOLTIP_3": "If the first value is true, then do the first block of statements. Otherwise, if the second value is true, do the second block of statements.",
	"CONTROLS_IF_TOOLTIP_4": "If the first value is true, then do the first block of statements. Otherwise, if the second value is true, do the second block of statements. If none of the values are true, do the last block of statements.",
	"CONTROLS_IF_MSG_IF": "if",
	"CONTROLS_IF_MSG_ELSEIF": "else if",
	"CONTROLS_IF_MSG_ELSE": "else",
	"CONTROLS_IF_IF_TOOLTIP": "Add, remove, or reorder sections to reconfigure this if block.",
	"CONTROLS_IF_ELSEIF_TOOLTIP": "Add a condition to the if block.",
	"CONTROLS_IF_ELSE_TOOLTIP": "Add a final, catch-all condition to the if block.",
	"IOS_OK": "OK",
	"IOS_CANCEL": "Cancel",
	"IOS_ERROR": "Error",
	"IOS_PROCEDURES_INPUTS": "INPUTS",
	"IOS_PROCEDURES_ADD_INPUT": "+ Add Input",
	"IOS_PROCEDURES_ALLOW_STATEMENTS": "Allow statements",
	"IOS_PROCEDURES_DUPLICATE_INPUTS_ERROR": "This function has duplicate inputs.",
	"IOS_VARIABLES_ADD_VARIABLE": "+ Add Variable",
	"IOS_VARIABLES_ADD_BUTTON": "Add",
	"IOS_VARIABLES_RENAME_BUTTON": "Rename",
	"IOS_VARIABLES_DELETE_BUTTON": "Delete",
	"IOS_VARIABLES_VARIABLE_NAME": "Variable name",
	"IOS_VARIABLES_EMPTY_NAME_ERROR": "You can't use an empty variable name.",
	"LOGIC_COMPARE_HELPURL": "https://en.wikipedia.org/wiki/Inequality_(mathematics)",
	"LOGIC_COMPARE_TOOLTIP_EQ": "Return true if both inputs equal each other.",
	"LOGIC_COMPARE_TOOLTIP_NEQ": "Return true if both inputs are not equal to each other.",
	"LOGIC_COMPARE_TOOLTIP_LT": "Return true if the first input is smaller than the second input.",
	"LOGIC_COMPARE_TOOLTIP_LTE": "Return true if the first input is smaller than or equal to the second input.",
	"LOGIC_COMPARE_TOOLTIP_GT": "Return true if the first input is greater than the second input.",
	"LOGIC_COMPARE_TOOLTIP_GTE": "Return true if the first input is greater than or equal to the second input.",
	"LOGIC_OPERATION_HELPURL": "https://github.com/google/blockly/wiki/Logic#logical-operations",
	"LOGIC_OPERATION_TOOLTIP_AND": "Return true if both inputs are true.",
	"LOGIC_OPERATION_AND": "and",
	"LOGIC_OPERATION_TOOLTIP_OR": "Return true if at least one of the inputs is true.",
	"LOGIC_OPERATION_OR": "or",
	"LOGIC_NEGATE_HELPURL": "https://github.com/google/blockly/wiki/Logic#not",
	"LOGIC_NEGATE_TITLE": "not %1",
	"LOGIC_NEGATE_TOOLTIP": "Returns true if the input is false. Returns false if the input is true.",
	"LOGIC_BOOLEAN_HELPURL": "https://github.com/google/blockly/wiki/Logic#values",
	"LOGIC_BOOLEAN_TRUE": "true",
	"LOGIC_BOOLEAN_FALSE": "false",
	"LOGIC_BOOLEAN_TOOLTIP": "Returns either true or false.",
	"LOGIC_NULL_HELPURL": "https://en.wikipedia.org/wiki/Nullable_type",
	"LOGIC_NULL": "null",
	"LOGIC_NULL_TOOLTIP": "Returns null.",
	"LOGIC_TERNARY_HELPURL": "https://en.wikipedia.org/wiki/%3F:",
	"LOGIC_TERNARY_CONDITION": "test",
	"LOGIC_TERNARY_IF_TRUE": "if true",
	"LOGIC_TERNARY_IF_FALSE": "if false",
	"LOGIC_TERNARY_TOOLTIP": "Check the condition in 'test'. If the condition is true, returns the 'if true' value; otherwise returns the 'if false' value.",
	"MATH_NUMBER_HELPURL": "https://en.wikipedia.org/wiki/Number",
	"MATH_NUMBER_TOOLTIP": "A number. AVAILABLE IN LICENSED VERSION",
	"MATH_ADDITION_SYMBOL": "+",
	"MATH_SUBTRACTION_SYMBOL": "-",
	"MATH_DIVISION_SYMBOL": "÷",
	"MATH_MULTIPLICATION_SYMBOL": "×",
	"MATH_POWER_SYMBOL": "^",
	"MATH_TRIG_SIN": "sin",
	"MATH_TRIG_COS": "cos",
	"MATH_TRIG_TAN": "tan",
	"MATH_TRIG_ASIN": "asin",
	"MATH_TRIG_ACOS": "acos",
	"MATH_TRIG_ATAN": "atan",
	"MATH_ARITHMETIC_HELPURL": "https://en.wikipedia.org/wiki/Arithmetic",
	"MATH_ARITHMETIC_TOOLTIP_ADD": "Return the sum of the two numbers.",
	"MATH_ARITHMETIC_TOOLTIP_MINUS": "Return the difference of the two numbers.",
	"MATH_ARITHMETIC_TOOLTIP_MULTIPLY": "Return the product of the two numbers.",
	"MATH_ARITHMETIC_TOOLTIP_DIVIDE": "Return the quotient of the two numbers.",
	"MATH_ARITHMETIC_TOOLTIP_POWER": "Return the first number raised to the power of the second number.",
	"MATH_SINGLE_HELPURL": "https://en.wikipedia.org/wiki/Square_root",
	"MATH_SINGLE_OP_ROOT": "square root",
	"MATH_SINGLE_TOOLTIP_ROOT": "Return the square root of a number. AVAILABLE IN LICENSED VERSION",
	"MATH_SINGLE_OP_ABSOLUTE": "absolute",
	"MATH_SINGLE_TOOLTIP_ABS": "Return the absolute value of a number.",
	"MATH_SINGLE_TOOLTIP_NEG": "Return the negation of a number.",
	"MATH_SINGLE_TOOLTIP_LN": "Return the natural logarithm of a number.",
	"MATH_SINGLE_TOOLTIP_LOG10": "Return the base 10 logarithm of a number.",
	"MATH_SINGLE_TOOLTIP_EXP": "Return e to the power of a number.",
	"MATH_SINGLE_TOOLTIP_POW10": "Return 10 to the power of a number.",
	"MATH_TRIG_HELPURL": "https://en.wikipedia.org/wiki/Trigonometric_functions",
	"MATH_TRIG_TOOLTIP_SIN": "Return the sine of a degree (not radian). AVAILABLE IN LICENSED VERSION",
	"MATH_TRIG_TOOLTIP_COS": "Return the cosine of a degree (not radian).",
	"MATH_TRIG_TOOLTIP_TAN": "Return the tangent of a degree (not radian).",
	"MATH_TRIG_TOOLTIP_ASIN": "Return the arcsine of a number.",
	"MATH_TRIG_TOOLTIP_ACOS": "Return the arccosine of a number.",
	"MATH_TRIG_TOOLTIP_ATAN": "Return the arctangent of a number.",
	"MATH_CONSTANT_HELPURL": "https://en.wikipedia.org/wiki/Mathematical_constant",
	"MATH_CONSTANT_TOOLTIP": "Return one of the common constants: π (3.141…), e (2.718…), φ (1.618…), sqrt(2) (1.414…), sqrt(½) (0.707…), or ∞ (infinity).",
	"MATH_IS_EVEN": "is even",
	"MATH_IS_ODD": "is odd",
	"MATH_IS_PRIME": "is prime",
	"MATH_IS_WHOLE": "is whole",
	"MATH_IS_POSITIVE": "is positive",
	"MATH_IS_NEGATIVE": "is negative",
	"MATH_IS_DIVISIBLE_BY": "is divisible by",
	"MATH_IS_TOOLTIP": "Check if a number is an even, odd, prime, whole, positive, negative, or if it is divisible by certain number. Returns true or false.",
	"MATH_CHANGE_HELPURL": "https://en.wikipedia.org/wiki/Programming_idiom#Incrementing_a_counter",
	"MATH_CHANGE_TITLE": "change %1 by %2",
	"MATH_CHANGE_TOOLTIP": "Add a number to variable '%1'.",
	"MATH_ROUND_HELPURL": "https://en.wikipedia.org/wiki/Rounding",
	"MATH_ROUND_TOOLTIP": "Round a number up or down.",
	"MATH_ROUND_OPERATOR_ROUND": "round",
	"MATH_ROUND_OPERATOR_ROUNDUP": "round up",
	"MATH_ROUND_OPERATOR_ROUNDDOWN": "round down",
	"MATH_ONLIST_HELPURL": "",
	"MATH_ONLIST_OPERATOR_SUM": "sum of list",
	"MATH_ONLIST_TOOLTIP_SUM": "Return the sum of all the numbers in the list.",
	"MATH_ONLIST_OPERATOR_MIN": "min of list",
	"MATH_ONLIST_TOOLTIP_MIN": "Return the smallest number in the list.",
	"MATH_ONLIST_OPERATOR_MAX": "max of list",
	"MATH_ONLIST_TOOLTIP_MAX": "Return the largest number in the list.",
	"MATH_ONLIST_OPERATOR_AVERAGE": "average of list",
	"MATH_ONLIST_TOOLTIP_AVERAGE": "Return the average (arithmetic mean) of the numeric values in the list.",
	"MATH_ONLIST_OPERATOR_MEDIAN": "median of list",
	"MATH_ONLIST_TOOLTIP_MEDIAN": "Return the median number in the list.",
	"MATH_ONLIST_OPERATOR_MODE": "modes of list",
	"MATH_ONLIST_TOOLTIP_MODE": "Return a list of the most common item(s) in the list.",
	"MATH_ONLIST_OPERATOR_STD_DEV": "standard deviation of list",
	"MATH_ONLIST_TOOLTIP_STD_DEV": "Return the standard deviation of the list.",
	"MATH_ONLIST_OPERATOR_RANDOM": "random item of list",
	"MATH_ONLIST_TOOLTIP_RANDOM": "Return a random element from the list.",
	"MATH_MODULO_HELPURL": "https://en.wikipedia.org/wiki/Modulo_operation",
	"MATH_MODULO_TITLE": "remainder of %1 ÷ %2",
	"MATH_MODULO_TOOLTIP": "Return the remainder from dividing the two numbers.",
	"MATH_CONSTRAIN_HELPURL": "https://en.wikipedia.org/wiki/Clamping_(graphics)",
	"MATH_CONSTRAIN_TITLE": "constrain %1 low %2 high %3",
	"MATH_CONSTRAIN_TOOLTIP": "Constrain a number to be between the specified limits (inclusive).",
	"MATH_RANDOM_INT_HELPURL": "https://en.wikipedia.org/wiki/Random_number_generation",
	"MATH_RANDOM_INT_TITLE": "random integer from %1 to %2",
	"MATH_RANDOM_INT_TOOLTIP": "Return a random integer between the two specified limits, inclusive.",
	"MATH_RANDOM_FLOAT_HELPURL": "https://en.wikipedia.org/wiki/Random_number_generation",
	"MATH_RANDOM_FLOAT_TITLE_RANDOM": "random fraction",
	"MATH_RANDOM_FLOAT_TOOLTIP": "Return a random fraction between 0.0 (inclusive) and 1.0 (exclusive).",
	"MATH_ATAN2_HELPURL": "https://en.wikipedia.org/wiki/Atan2",
	"MATH_ATAN2_TITLE": "atan2 of X:%1 Y:%2",
	"MATH_ATAN2_TOOLTIP": "Return the arctangent of point (X, Y) in degrees from -180 to 180.",
	"TEXT_TEXT_HELPURL": "https://en.wikipedia.org/wiki/String_(computer_science)",
	"TEXT_TEXT_TOOLTIP": "A letter, word, or line of text.",
	"TEXT_JOIN_HELPURL": "https://github.com/google/blockly/wiki/Text#text-creation",
	"TEXT_JOIN_TITLE_CREATEWITH": "create text with",
	"TEXT_JOIN_TOOLTIP": "Create a piece of text by joining together any number of items.",
	"TEXT_CREATE_JOIN_TITLE_JOIN": "join",
	"TEXT_CREATE_JOIN_TOOLTIP": "Add, remove, or reorder sections to reconfigure this text block.",
	"TEXT_CREATE_JOIN_ITEM_TOOLTIP": "Add an item to the text.",
	"TEXT_APPEND_HELPURL": "https://github.com/google/blockly/wiki/Text#text-modification",
	"TEXT_APPEND_TITLE": "to %1 append text %2",
	"TEXT_APPEND_TOOLTIP": "Append some text to variable '%1'.",
	"TEXT_LENGTH_HELPURL": "https://github.com/google/blockly/wiki/Text#text-modification",
	"TEXT_LENGTH_TITLE": "length of %1",
	"TEXT_LENGTH_TOOLTIP": "Returns the number of letters (including spaces) in the provided text.",
	"TEXT_ISEMPTY_HELPURL": "https://github.com/google/blockly/wiki/Text#checking-for-empty-text",
	"TEXT_ISEMPTY_TITLE": "%1 is empty",
	"TEXT_ISEMPTY_TOOLTIP": "Returns true if the provided text is empty.",
	"TEXT_INDEXOF_HELPURL": "https://github.com/google/blockly/wiki/Text#finding-text",
	"TEXT_INDEXOF_TOOLTIP": "Returns the index of the first/last occurrence of the first text in the second text. Returns %1 if text is not found.",
	"TEXT_INDEXOF_TITLE": "in text %1 %2 %3",
	"TEXT_INDEXOF_OPERATOR_FIRST": "find first occurrence of text",
	"TEXT_INDEXOF_OPERATOR_LAST": "find last occurrence of text",
	"TEXT_CHARAT_HELPURL": "https://github.com/google/blockly/wiki/Text#extracting-text",
	"TEXT_CHARAT_TITLE": "in text %1 %2",
	"TEXT_CHARAT_FROM_START": "get letter #",
	"TEXT_CHARAT_FROM_END": "get letter # from end",
	"TEXT_CHARAT_FIRST": "get first letter",
	"TEXT_CHARAT_LAST": "get last letter",
	"TEXT_CHARAT_RANDOM": "get random letter",
	"TEXT_CHARAT_TAIL": "",
	"TEXT_CHARAT_TOOLTIP": "Returns the letter at the specified position.",
	"TEXT_GET_SUBSTRING_TOOLTIP": "Returns a specified portion of the text.",
	"TEXT_GET_SUBSTRING_HELPURL": "https://github.com/google/blockly/wiki/Text#extracting-a-region-of-text",
	"TEXT_GET_SUBSTRING_INPUT_IN_TEXT": "in text",
	"TEXT_GET_SUBSTRING_START_FROM_START": "get substring from letter #",
	"TEXT_GET_SUBSTRING_START_FROM_END": "get substring from letter # from end",
	"TEXT_GET_SUBSTRING_START_FIRST": "get substring from first letter",
	"TEXT_GET_SUBSTRING_END_FROM_START": "to letter #",
	"TEXT_GET_SUBSTRING_END_FROM_END": "to letter # from end",
	"TEXT_GET_SUBSTRING_END_LAST": "to last letter",
	"TEXT_GET_SUBSTRING_TAIL": "",
	"TEXT_CHANGECASE_HELPURL": "https://github.com/google/blockly/wiki/Text#adjusting-text-case",
	"TEXT_CHANGECASE_TOOLTIP": "Return a copy of the text in a different case.",
	"TEXT_CHANGECASE_OPERATOR_UPPERCASE": "to UPPER CASE",
	"TEXT_CHANGECASE_OPERATOR_LOWERCASE": "to lower case",
	"TEXT_CHANGECASE_OPERATOR_TITLECASE": "to Title Case",
	"TEXT_TRIM_HELPURL": "https://github.com/google/blockly/wiki/Text#trimming-removing-spaces",
	"TEXT_TRIM_TOOLTIP": "Return a copy of the text with spaces removed from one or both ends.",
	"TEXT_TRIM_OPERATOR_BOTH": "trim spaces from both sides of",
	"TEXT_TRIM_OPERATOR_LEFT": "trim spaces from left side of",
	"TEXT_TRIM_OPERATOR_RIGHT": "trim spaces from right side of",
	"TEXT_PRINT_HELPURL": "https://github.com/google/blockly/wiki/Text#printing-text",
	"TEXT_PRINT_TITLE": "print %1",
	"TEXT_PRINT_TOOLTIP": "Print the specified text, number or other value.",
	"TEXT_PROMPT_HELPURL": "https://github.com/google/blockly/wiki/Text#getting-input-from-the-user",
	"TEXT_PROMPT_TYPE_TEXT": "prompt for text with message",
	"TEXT_PROMPT_TYPE_NUMBER": "prompt for number with message",
	"TEXT_PROMPT_TOOLTIP_NUMBER": "Prompt for user for a number.",
	"TEXT_PROMPT_TOOLTIP_TEXT": "Prompt for user for some text.",
	"TEXT_COUNT_MESSAGE0": "count %1 in %2",
	"TEXT_COUNT_HELPURL": "https://github.com/google/blockly/wiki/Text#counting-substrings",
	"TEXT_COUNT_TOOLTIP": "Count how many times some text occurs within some other text.",
	"TEXT_REPLACE_MESSAGE0": "replace %1 with %2 in %3",
	"TEXT_REPLACE_HELPURL": "https://github.com/google/blockly/wiki/Text#replacing-substrings",
	"TEXT_REPLACE_TOOLTIP": "Replace all occurances of some text within some other text.",
	"TEXT_REVERSE_MESSAGE0": "reverse %1",
	"TEXT_REVERSE_HELPURL": "https://github.com/google/blockly/wiki/Text#reversing-text",
	"TEXT_REVERSE_TOOLTIP": "Reverses the order of the characters in the text.",
	"LISTS_CREATE_EMPTY_HELPURL": "https://github.com/google/blockly/wiki/Lists#create-empty-list",
	"LISTS_CREATE_EMPTY_TITLE": "create empty list",
	"LISTS_CREATE_EMPTY_TOOLTIP": "Returns a list, of length 0, containing no data records",
	"LISTS_CREATE_WITH_HELPURL": "https://github.com/google/blockly/wiki/Lists#create-list-with",
	"LISTS_CREATE_WITH_TOOLTIP": "Create a list with any number of items.",
	"LISTS_CREATE_WITH_INPUT_WITH": "create list with",
	"LISTS_CREATE_WITH_CONTAINER_TITLE_ADD": "list",
	"LISTS_CREATE_WITH_CONTAINER_TOOLTIP": "Add, remove, or reorder sections to reconfigure this list block.",
	"LISTS_CREATE_WITH_ITEM_TOOLTIP": "Add an item to the list.",
	"LISTS_REPEAT_HELPURL": "https://github.com/google/blockly/wiki/Lists#create-list-with",
	"LISTS_REPEAT_TOOLTIP": "Creates a list consisting of the given value repeated the specified number of times.",
	"LISTS_REPEAT_TITLE": "create list with item %1 repeated %2 times",
	"LISTS_LENGTH_HELPURL": "https://github.com/google/blockly/wiki/Lists#length-of",
	"LISTS_LENGTH_TITLE": "length of %1",
	"LISTS_LENGTH_TOOLTIP": "Returns the length of a list.",
	"LISTS_ISEMPTY_HELPURL": "https://github.com/google/blockly/wiki/Lists#is-empty",
	"LISTS_ISEMPTY_TITLE": "%1 is empty",
	"LISTS_ISEMPTY_TOOLTIP": "Returns true if the list is empty.",
	"LISTS_INLIST": "in list",
	"LISTS_INDEX_OF_HELPURL": "https://github.com/google/blockly/wiki/Lists#getting-items-from-a-list",
	"LISTS_INDEX_OF_FIRST": "find first occurrence of item",
	"LISTS_INDEX_OF_LAST": "find last occurrence of item",
	"LISTS_INDEX_OF_TOOLTIP": "Returns the index of the first/last occurrence of the item in the list. Returns %1 if item is not found.",
	"LISTS_GET_INDEX_GET": "get",
	"LISTS_GET_INDEX_GET_REMOVE": "get and remove",
	"LISTS_GET_INDEX_REMOVE": "remove",
	"LISTS_GET_INDEX_FROM_START": "#",
	"LISTS_GET_INDEX_FROM_END": "# from end",
	"LISTS_GET_INDEX_FIRST": "first",
	"LISTS_GET_INDEX_LAST": "last",
	"LISTS_GET_INDEX_RANDOM": "random",
	"LISTS_GET_INDEX_TAIL": "",
	"LISTS_INDEX_FROM_START_TOOLTIP": "%1 is the first item.",
	"LISTS_INDEX_FROM_END_TOOLTIP": "%1 is the last item.",
	"LISTS_GET_INDEX_TOOLTIP_GET_FROM": "Returns the item at the specified position in a list.",
	"LISTS_GET_INDEX_TOOLTIP_GET_FIRST": "Returns the first item in a list.",
	"LISTS_GET_INDEX_TOOLTIP_GET_LAST": "Returns the last item in a list.",
	"LISTS_GET_INDEX_TOOLTIP_GET_RANDOM": "Returns a random item in a list.",
	"LISTS_GET_INDEX_TOOLTIP_GET_REMOVE_FROM": "Removes and returns the item at the specified position in a list.",
	"LISTS_GET_INDEX_TOOLTIP_GET_REMOVE_FIRST": "Removes and returns the first item in a list.",
	"LISTS_GET_INDEX_TOOLTIP_GET_REMOVE_LAST": "Removes and returns the last item in a list.",
	"LISTS_GET_INDEX_TOOLTIP_GET_REMOVE_RANDOM": "Removes and returns a random item in a list.",
	"LISTS_GET_INDEX_TOOLTIP_REMOVE_FROM": "Removes the item at the specified position in a list.",
	"LISTS_GET_INDEX_TOOLTIP_REMOVE_FIRST": "Removes the first item in a list.",
	"LISTS_GET_INDEX_TOOLTIP_REMOVE_LAST": "Removes the last item in a list.",
	"LISTS_GET_INDEX_TOOLTIP_REMOVE_RANDOM": "Removes a random item in a list.",
	"LISTS_SET_INDEX_HELPURL": "https://github.com/google/blockly/wiki/Lists#in-list--set",
	"LISTS_SET_INDEX_SET": "set",
	"LISTS_SET_INDEX_INSERT": "insert at",
	"LISTS_SET_INDEX_INPUT_TO": "as",
	"LISTS_SET_INDEX_TOOLTIP_SET_FROM": "Sets the item at the specified position in a list.",
	"LISTS_SET_INDEX_TOOLTIP_SET_FIRST": "Sets the first item in a list.",
	"LISTS_SET_INDEX_TOOLTIP_SET_LAST": "Sets the last item in a list.",
	"LISTS_SET_INDEX_TOOLTIP_SET_RANDOM": "Sets a random item in a list.",
	"LISTS_SET_INDEX_TOOLTIP_INSERT_FROM": "Inserts the item at the specified position in a list.",
	"LISTS_SET_INDEX_TOOLTIP_INSERT_FIRST": "Inserts the item at the start of a list.",
	"LISTS_SET_INDEX_TOOLTIP_INSERT_LAST": "Append the item to the end of a list.",
	"LISTS_SET_INDEX_TOOLTIP_INSERT_RANDOM": "Inserts the item randomly in a list.",
	"LISTS_GET_SUBLIST_HELPURL": "https://github.com/google/blockly/wiki/Lists#getting-a-sublist",
	"LISTS_GET_SUBLIST_START_FROM_START": "get sub-list from #",
	"LISTS_GET_SUBLIST_START_FROM_END": "get sub-list from # from end",
	"LISTS_GET_SUBLIST_START_FIRST": "get sub-list from first",
	"LISTS_GET_SUBLIST_END_FROM_START": "to #",
	"LISTS_GET_SUBLIST_END_FROM_END": "to # from end",
	"LISTS_GET_SUBLIST_END_LAST": "to last",
	"LISTS_GET_SUBLIST_TAIL": "",
	"LISTS_GET_SUBLIST_TOOLTIP": "Creates a copy of the specified portion of a list.",
	"LISTS_SORT_HELPURL": "https://github.com/google/blockly/wiki/Lists#sorting-a-list",
	"LISTS_SORT_TITLE": "sort %1 %2 %3",
	"LISTS_SORT_TOOLTIP": "Sort a copy of a list.",
	"LISTS_SORT_ORDER_ASCENDING": "ascending",
	"LISTS_SORT_ORDER_DESCENDING": "descending",
	"LISTS_SORT_TYPE_NUMERIC": "numeric",
	"LISTS_SORT_TYPE_TEXT": "alphabetic",
	"LISTS_SORT_TYPE_IGNORECASE": "alphabetic, ignore case",
	"LISTS_SPLIT_HELPURL": "https://github.com/google/blockly/wiki/Lists#splitting-strings-and-joining-lists",
	"LISTS_SPLIT_LIST_FROM_TEXT": "make list from text",
	"LISTS_SPLIT_TEXT_FROM_LIST": "make text from list",
	"LISTS_SPLIT_WITH_DELIMITER": "with delimiter",
	"LISTS_SPLIT_TOOLTIP_SPLIT": "Split text into a list of texts, breaking at each delimiter.",
	"LISTS_SPLIT_TOOLTIP_JOIN": "Join a list of texts into one text, separated by a delimiter.",
	"LISTS_REVERSE_HELPURL": "https://github.com/google/blockly/wiki/Lists#reversing-a-list",
	"LISTS_REVERSE_MESSAGE0": "reverse %1",
	"LISTS_REVERSE_TOOLTIP": "Reverse a copy of a list.",
	"ORDINAL_NUMBER_SUFFIX": "",
	"VARIABLES_GET_HELPURL": "https://github.com/google/blockly/wiki/Variables#get",
	"VARIABLES_GET_TOOLTIP": "Returns the value of this variable.",
	"VARIABLES_GET_CREATE_SET": "Create 'set %1'",
	"VARIABLES_SET_HELPURL": "https://github.com/google/blockly/wiki/Variables#set",
	"VARIABLES_SET": "set %1 to %2",
	"VARIABLES_SET_TOOLTIP": "Sets this variable to be equal to the input.",
	"VARIABLES_SET_CREATE_GET": "Create 'get %1'",
	"PROCEDURES_DEFNORETURN_HELPURL": "https://en.wikipedia.org/wiki/Subroutine",
	"PROCEDURES_DEFNORETURN_TITLE": "to",
	"PROCEDURES_DEFNORETURN_PROCEDURE": "do something",
	"PROCEDURES_BEFORE_PARAMS": "with:",
	"PROCEDURES_CALL_BEFORE_PARAMS": "with:",
	"PROCEDURES_DEFNORETURN_DO": "",
	"PROCEDURES_DEFNORETURN_TOOLTIP": "Creates a function with no output.",
	"PROCEDURES_DEFNORETURN_COMMENT": "Describe this function...",
	"PROCEDURES_DEFRETURN_HELPURL": "https://en.wikipedia.org/wiki/Subroutine",
	"PROCEDURES_DEFRETURN_RETURN": "return",
	"PROCEDURES_DEFRETURN_TOOLTIP": "Creates a function with an output.",
	"PROCEDURES_ALLOW_STATEMENTS": "allow statements",
	"PROCEDURES_DEF_DUPLICATE_WARNING": "Warning: This function has duplicate parameters.",
	"PROCEDURES_CALLNORETURN_HELPURL": "https://en.wikipedia.org/wiki/Subroutine",
	"PROCEDURES_CALLNORETURN_TOOLTIP": "Run the user-defined function '%1'.",
	"PROCEDURES_CALLRETURN_HELPURL": "https://en.wikipedia.org/wiki/Subroutine",
	"PROCEDURES_CALLRETURN_TOOLTIP": "Run the user-defined function '%1' and use its output.",
	"PROCEDURES_MUTATORCONTAINER_TITLE": "inputs",
	"PROCEDURES_MUTATORCONTAINER_TOOLTIP": "Add, remove, or reorder inputs to this function.",
	"PROCEDURES_MUTATORARG_TITLE": "input name:",
	"PROCEDURES_MUTATORARG_TOOLTIP": "Add an input to the function.",
	"PROCEDURES_HIGHLIGHT_DEF": "Highlight function definition",
	"PROCEDURES_CREATE_DO": "Create '%1'",
	"PROCEDURES_IFRETURN_TOOLTIP": "If a value is true, then return a second value.",
	"PROCEDURES_IFRETURN_HELPURL": "http://c2.com/cgi/wiki?GuardClause",
	"PROCEDURES_IFRETURN_WARNING": "Warning: This block may be used only within a function definition.",
	"WORKSPACE_COMMENT_DEFAULT_TEXT": "Say something...",
	"WORKSPACE_ARIA_LABEL": "Blockly Workspace",
	"COLLAPSED_WARNINGS_WARNING": "Collapsed blocks contain warnings.",
}
